import {Tooltip} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {assert} from 'Asserts';
import {Label, Main, Pane, Step, Steps} from 'bulk-workflows/BulkWorkflowComponents';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import FolderSelector, {DEFAULT_PROJECT_FOLDER_LABEL_TEXT} from 'file-browser/FolderSelector';
import MultipleFilesSelector from 'file-browser/MultipleFilesSelector';
import TagSchemaSelector from 'schemas/shared/TagSchemaSelector';
import {Filter, FilterKey, Operator} from 'search/SearchConstants';
import FormGroup from 'ui-library/bs/FormGroup';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import * as BulkTranslationActions from './BulkTranslationActions';
import {BulkTranslation} from './BulkTranslationConstants';
import * as BulkTranslationUtil from './BulkTranslationUtil';
import BulkTranslationErrors from './components/BulkTranslationErrors';
import BulkTranslationSummary from './components/BulkTranslationSummary';
import TranslationSpecEditor from './components/TranslationSpecEditor';

const PROJECT_FOLDER_LABEL_TEXT = `Select output ${DEFAULT_PROJECT_FOLDER_LABEL_TEXT.toLowerCase()}`;

const getFileFilters = (tagSchemaId: string): Array<Filter> => [
  {
    key: FilterKey.TYPES,
    operator: Operator.IS_ONE_OF,
    value: [ModelTypeConstants.SEQUENCE],
  },
  {
    key: FilterKey.SCHEMA,
    operator: Operator.IS_ONE_OF,
    value: [tagSchemaId],
  },
];

interface BulkTranslationEditorProps {
  bulkTranslation: BulkTranslation;
}

class BulkTranslationEditor extends React.Component<BulkTranslationEditorProps> {
  _handleSequenceTagSchemaChange = (tagSchema: {id: string} | null): void => {
    assert(tagSchema != null, 'TagSchemaSelector returned a null tag schema');
    BulkTranslationActions.update({sequenceTagSchemaId: tagSchema.id});
  };

  _handleSelectedIdsChange = (selectedIds: Array<string>): void => {
    BulkTranslationActions.update({selectedIds});
  };

  _handleOutputFolderChange = (e: {target: {value: string}}): void => {
    BulkTranslationActions.update({outputFolderId: e.target.value});
  };

  override render(): JSX.Element {
    // TODO(T8838): Refactor into child components once the bulk workflow UI components are fully implemented
    const {bulkTranslation} = this.props;
    const errors = BulkTranslationUtil.getErrors(bulkTranslation);
    return (
      <Main>
        <Pane>
          <Steps>
            <Step text="Select files to translate">
              <FormGroup>
                <Label>
                  Sequence Schema
                  <Tooltip
                    label="Only schemas with a translation link field may be used for bulk translation"
                    withinPortal={true}
                  >
                    <BenchlingIcon className="u-mini-spacing-left" icon="circle-question" />
                  </Tooltip>
                </Label>
                <TagSchemaSelector
                  requireTranslationLink={true}
                  oligoAwareFolderItemTypes={[ModelTypeConstants.SEQUENCE]}
                  onChange={this._handleSequenceTagSchemaChange}
                  selectedTagSchemaId={bulkTranslation.sequenceTagSchemaId}
                />
              </FormGroup>
              {bulkTranslation.sequenceTagSchemaId ? (
                <FormGroup>
                  <Label>Sequence files and folders</Label>
                  <MultipleFilesSelector
                    value={bulkTranslation.selectedIds}
                    onChange={this._handleSelectedIdsChange}
                    folderFilter={{
                      level: 'read',
                      includeHiddenProjects: false,
                      showInventory: true,
                      showNotebook: true,
                      showRegistries: true,
                    }}
                    fileFilter={getFileFilters(bulkTranslation.sequenceTagSchemaId)}
                    fetchLimit={20}
                  />
                </FormGroup>
              ) : null}
            </Step>
            <Step text="Select translation link fields">
              <p>All translations use the standard genetic code.</p>
              {bulkTranslation.translationSpecs.map((translationSpec, translationSpecIndex) => (
                <TranslationSpecEditor
                  key={translationSpecIndex}
                  translationSpec={translationSpec}
                  translationSpecIndex={translationSpecIndex}
                />
              ))}
            </Step>
            <Step text={PROJECT_FOLDER_LABEL_TEXT}>
              <FolderSelector
                requireSubfolderCreatableAuth={true}
                showInventory={true}
                value={bulkTranslation.outputFolderId}
                onChange={this._handleOutputFolderChange}
                unsetOnFetchFail={true}
              />
            </Step>
          </Steps>
        </Pane>
        <div className={css(styles.errorsAndSummary)}>
          {errors.length > 0 && (
            <Pane header="Translation errors" className={css(styles.errors)}>
              <BulkTranslationErrors errors={errors} />
            </Pane>
          )}
          <Pane header="Translation summary" className={css(styles.summary)}>
            <BulkTranslationSummary bulkTranslation={bulkTranslation} />
          </Pane>
        </div>
      </Main>
    );
  }
}

const styles = StyleSheet.create({
  errorsAndSummary: {flex: 1, overflow: 'hidden', flexDirection: 'column', display: 'flex'},
  errors: {flex: 1, borderRight: '0px'},
  summary: {flex: 3},
});

export default BulkTranslationEditor;
